import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { LucideIcon } from "../../../../icon/LucideIcon";
import styles from "./FoldableFilters.module.css";

interface FoldableFilterProps {
  defaultOpen?: boolean;
  controls?: React.ReactNode;
  label: React.ReactNode;
  children: ReactNode;
}

// @author @CorradoSurmanowicz
export const FoldableFilters = ({ controls, children, label, defaultOpen = false }: FoldableFilterProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

  const memoizedDefaultOpen = useMemo(() => defaultOpen, [defaultOpen]);
  // Update isOpen only when defaultOpen prop changes
  useEffect(() => {
    setIsOpen(memoizedDefaultOpen);
  }, [memoizedDefaultOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen((prevState) => !prevState);
  }, []);

  return (
    <div
      style={{
        borderRadius: "4px",
        marginBottom: "5px",
        minHeight: "fit-content",
        overflow: "hidden",
      }}
    >
      <div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleToggle();
        }}
        className="flex align-center gap-5 form form-control"
        style={{
          cursor: "pointer",
          userSelect: "none",
          padding: 5,
          background: "var(--white)",
          ...(isOpen && {
            borderRadius: "4px 4px 0px 0px",
            borderBottom: "1px dashed var(--gray-300)",
          }),
        }}
      >
        <div style={{ width: "max-content" }}>
          <LucideIcon name={isOpen ? "chevron-down" : "chevron-right"} />
        </div>
        <div style={{ width: "max-content" }}>
          <LucideIcon name="filter" color="var(--primary)" />
        </div>
        <h5 style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", margin: 0 }}>
          {label}
        </h5>
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {controls}
        </div>
      </div>
      {isOpen && (
        <div
          className={`${styles.FoldableFiltersContent} flex col-nowrap`}
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{
            width: "100%",
            height: "fit-content",
            overflow: "hidden",
            padding: "2.5px 2.5px 0px 2.5px",
            borderLeft: "1px solid var(--gray-300)",
            borderRight: "1px solid var(--gray-300)",
            borderBottom: "1px solid var(--gray-300)",
            borderRadius: "0px 0px 4px 4px",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};
